import { useState } from "react";
import { WorkspaceDocument } from "@/models/workspace/Workspace";
import { useSharePointContext } from "../providers/SharePointContext";
import useAzureStorage from "./useAzureStorage";
import {
  showErrorToast,
  showSuccessToast,
  showProgressToast,
  updateProgressToast,
} from "@/components/custom-toasts/toasts";
import { useWorkspaceContext } from "../providers/WorkspaceContext";
import { findFolderById } from "../utils/folderUtils";
import useWorkspace from "@/hooks/useWorkspace";
import { logger } from "@/lib/logger";
import { UploadSource } from "@/components/upload/types/upload-item";
import { useUploadHub } from "./useUploadHub";
import { useDeletedFilesDialogStore } from "@/stores/deleted-files-dialog.store";
import { useSyncReportDialogStore } from "@/stores/sync-report-dialog.store";
import { useChangeIdentification } from "./useChangeIdentification";
import { useSyncMutation } from "./useSyncMutation";
import {
  CloudFileSyncFetch,
  FileSyncOptions,
  hasChangesToSync,
  SyncResultVariant,
  SyncResults,
} from "@/types/sync.types";
import { isActiveRevisionSearchIndexStale } from "../utils/workspaceItemUtils";
import { GraphCallOptions } from "@/services/microsoft/graphApiService";
import { GraphRetryLog } from "@/services/microsoft/graphFetch";

/**
 * Hook for handling file synchronization with SharePoint/OneDrive
 */
const useFileSync = () => {
  const { fetchFileForSync, uploadFileToSharePoint } = useSharePointContext();
  const { downloadFile } = useAzureStorage();
  const {
    handleUpdateDocumentMetadata,
    handleUpdateUploadedFile,
    workspace,
    myUser,
    tenantUsers,
    isUserOwner,
    isUserAdmin,
    handleDeleteDocuments,
  } = useWorkspaceContext();
  const { updateFolderMetadata } = useWorkspace();
  const { handleFolderUpload, createFileListFromDownloadedFiles } =
    useUploadHub();

  const isAdminOrOwner = isUserOwner || isUserAdmin();

  const [documentsSynchronizing, setDocumentsSynchronizing] = useState<
    string[]
  >([]);

  // Zustand store for missing files dialog (used for initial SharePoint sync flow)
  const {
    deletedFiles,
    setIsDeleting: setIsDeletingFiles,
    setIsOpen: setDeletedFilesDialogOpen,
    removeDeletedFiles,
  } = useDeletedFilesDialogStore();

  // Zustand store for sync report dialog
  const { openDialog: openSyncReportDialog } = useSyncReportDialogStore();

  // Change identification hook
  const { identifyFolderChanges, identifyAllChanges } =
    useChangeIdentification(workspace);

  const parseTimestamp = (value?: string | Date): number => {
    if (!value) return 0;
    const parsed = new Date(value).getTime();
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const getLatestRevisionTimestamp = (document: WorkspaceDocument): number => {
    const revisionTimestamps = (document.revisions ?? []).map((revision) =>
      parseTimestamp(revision.updatedAt)
    );
    const latestRevisionTimestamp =
      revisionTimestamps.length > 0 ? Math.max(...revisionTimestamps) : 0;

    // Fallback to document timestamp for legacy documents without revisions.
    return latestRevisionTimestamp || parseTimestamp(document.updatedAt);
  };

  /**
   * Syncs a single document from SharePoint/OneDrive
   */
  const handleSharePointFileSyncClick = async (
    document: WorkspaceDocument,
    { graphOptions, cloudLastModified }: FileSyncOptions = {}
  ): Promise<{
    title: string;
    description: string;
    variant: SyncResultVariant;
  }> => {
    setDocumentsSynchronizing((prev) => [...prev, document.id]);

    const workspaceFileLastModified = getLatestRevisionTimestamp(document);
    const mustRepairSearchIndex = isActiveRevisionSearchIndexStale(document);
    // Download only when the cloud copy is newer, or the index needs repair.
    const shouldDownload = (sharePointFileModified: number) =>
      mustRepairSearchIndex ||
      !workspaceFileLastModified ||
      sharePointFileModified > workspaceFileLastModified;

    // The folder listing already showed this file unchanged: skip Graph.
    const fetched: CloudFileSyncFetch =
      cloudLastModified !== undefined && !shouldDownload(cloudLastModified)
        ? { kind: "unchanged" }
        : await fetchFileForSync(document, shouldDownload, graphOptions);
    if (fetched.kind !== "downloaded") {
      setDocumentsSynchronizing((prev) =>
        prev.filter((docId) => docId !== document.id)
      );
    }
    if (fetched.kind === "missing") {
      return {
        title: "Fil ikke fundet",
        description: `Filen med ID: ${
          document.sharepointFileId || document.blobName
        } kunne ikke tilgås på SharePoint/OneDrive.`,
        variant: SyncResultVariant.MISSING,
      };
    }
    if (fetched.kind === "failed") {
      return {
        title: "Synkronisering mislykkedes",
        description: `Filen kunne ikke hentes (${fetched.reason}). Prøv igen om lidt.`,
        variant: SyncResultVariant.FAILED,
      };
    }
    if (fetched.kind === "unchanged") {
      return {
        title: "Ingen opdatering nødvendig",
        description: "SharePoint-filen er ikke nyere end den eksisterende fil.",
        variant: SyncResultVariant.DEFAULT,
      };
    }

    const updated = await handleUpdateUploadedFile(fetched.file, document.id);
    setDocumentsSynchronizing((prev) =>
      prev.filter((docId) => docId !== document.id)
    );

    if (!updated.ok) {
      return {
        title: "Synkronisering afvist",
        description: updated.message,
        variant: SyncResultVariant.DESTRUCTIVE,
      };
    }

    return {
      title: "Synkronisering gennemført",
      description: "Filen er blevet opdateret fra SharePoint.",
      variant: SyncResultVariant.DEFAULT,
    };
  };

  // Sync mutation hook - always called but guards internally
  const { executeSyncMutations } = useSyncMutation(
    workspace
      ? {
          workspace,
          isAdminOrOwner,
          tenantUsers,
          currentUserId: myUser?.id,
          handleSharePointFileSyncClick,
          handleFolderUpload,
          createFileListFromDownloadedFiles,
        }
      : null
  );

  /**
   * Shows the sync report dialog when there is something to display.
   * Falls back to a brief error toast for hard errors; otherwise opens the
   * persistent report dialog so the user can review results at their leisure.
   */
  const resolveInitiatorName = (): string | undefined => {
    if (!myUser) return undefined;
    const name = [myUser.firstName, myUser.lastName].filter(Boolean).join(" ");
    return name || myUser.email;
  };

  const showSyncReport = (
    results: SyncResults,
    context?: { workspaceName?: string; folderName?: string; workspaceId?: string; folderId?: string }
  ) => {
    const hasAnything =
      results.existingDocumentsSynced > 0 ||
      results.existingDocumentsNoUpdate > 0 ||
      results.newDocumentsAdded > 0 ||
      results.newFoldersCreated > 0 ||
      results.folderNamesUpdated > 0 ||
      results.skippedMissingDocuments.length > 0 ||
      results.missingFilesForAdminReview.length > 0 ||
      results.errors.length > 0;

    if (!hasAnything) return;

    openSyncReportDialog({
      ...results,
      reportContext: {
        ...context,
        initiatedByName: resolveInitiatorName(),
        initiatedById: myUser?.id,
      },
    });
  };

  /**
   * Unlinks a folder from SharePoint/OneDrive
   */
  const handleUnlinkFolder = async (folderId: string) => {
    if (!workspace) {
      logger.warn("Folder unlink started without workspace data", {
        operation: "unlinkFolder",
        folderId,
      });
      showErrorToast("Kunne ikke fjerne link: Workspace ikke fundet.", "Fejl");
      return;
    }

    try {
      await updateFolderMetadata.mutateAsync({
        workspaceId: workspace.id,
        folderId: folderId,
        requestBody: {
          folderSource: UploadSource.LOCAL,
          driveId: undefined,
          siteId: undefined,
          sharepointFileId: undefined,
          name: undefined,
        },
      });

      showSuccessToast("Mappe er nu fjernet fra OneDrive/SharePoint");
    } catch (error) {
      showErrorToast("Kunne ikke fjerne link til OneDrive/SharePoint", "Fejl");
      logger.error("Failed to unlink folder from SharePoint/OneDrive", {
        operation: "unlinkFolder",
        error,
        workspaceId: workspace.id,
        folderId,
      });
    }
  };

  /**
   * Syncs a single folder and its subfolders recursively
   */
  const handleSyncFolder = async (folderId: string) => {
    if (!workspace || !myUser?.id || !executeSyncMutations) {
      logger.warn("Folder sync started without workspace or user data", {
        operation: "syncFolder",
        folderId,
        hasWorkspace: Boolean(workspace),
        hasUser: Boolean(myUser?.id),
      });
      showErrorToast(
        "Kunne ikke synkronisere: Workspace eller bruger ikke fundet.",
        "Fejl"
      );
      return;
    }

    const folder = findFolderById(workspace, folderId);
    if (!folder) {
      logger.warn("Folder to sync is not in the workspace", {
        operation: "syncFolder",
        workspaceId: workspace.id,
        folderId,
      });
      showErrorToast("Mappe ikke fundet.", "Fejl");
      return;
    }

    if (!folder.sharepointFileId || !folder.driveId) {
      logger.warn("Folder to sync is not linked to SharePoint/OneDrive", {
        operation: "syncFolder",
        workspaceId: workspace.id,
        folderId,
      });
      showErrorToast("Mappen er ikke knyttet til OneDrive/SharePoint.", "Fejl");
      return;
    }

    // Initialize progress toast
    const progressToastId = showProgressToast(
      "Identificerer ændringer...",
      "Synkroniserer mappe"
    );

    // Phase 1: Identification
    updateProgressToast(
      progressToastId,
      `Identificerer ændringer i mappe: ${folder.name}...`,
      "Identificerer ændringer"
    );

    // One retry log per sync: Graph throttling logs one warning, not one per call.
    const retryLog = new GraphRetryLog();
    const graphOptions: GraphCallOptions = { retryLog };

    let results: SyncResults;
    try {
      const changes = await identifyFolderChanges(
        folder,
        (message) => {
          updateProgressToast(
            progressToastId,
            message,
            "Identificerer ændringer"
          );
        },
        graphOptions
      );

      if (!hasChangesToSync(changes)) {
        const { toast } = await import("@capacit-com/lara-law-shared-ui");
        toast.dismiss(progressToastId);
        showSuccessToast(
          "Ingen filer eller mapper at synkronisere i denne mappe.",
          "Synkroniseringsrapport"
        );
        return;
      }

      // Phase 2: Mutation
      setDocumentsSynchronizing(
        changes.existingDocumentsToSync.map((doc) => doc.id)
      );

      results = await executeSyncMutations(changes, {
        progressToastId,
        parentFolderId: folderId,
        graphOptions,
      });
    } finally {
      retryLog.flush({ workspaceId: workspace.id, folderId });
    }

    // Dismiss progress toast
    const { toast } = await import("@capacit-com/lara-law-shared-ui");
    toast.dismiss(progressToastId);

    showSyncReport(results, {
      workspaceId: workspace.id,
      workspaceName: workspace.name,
      folderId: folder.id,
      folderName: folder.name,
    });
    setDocumentsSynchronizing([]);
  };

  /**
   * Syncs all SharePoint/OneDrive folders in the workspace
   */
  const handleSyncAll = async () => {
    if (!workspace || !myUser?.id || !executeSyncMutations) {
      logger.warn("Workspace sync started without workspace or user data", {
        operation: "syncAllFolders",
        hasWorkspace: Boolean(workspace),
        hasUser: Boolean(myUser?.id),
      });
      showErrorToast(
        "Kunne ikke synkronisere: Workspace eller bruger ikke fundet.",
        "Fejl"
      );
      return;
    }

    // Initialize progress toast
    const progressToastId = showProgressToast(
      "Identificerer ændringer...",
      "Synkroniserer"
    );

    // Phase 1: Identification
    updateProgressToast(
      progressToastId,
      "Identificerer ændringer i mapper og filer...",
      "Identificerer ændringer"
    );

    // One retry log per sync: Graph throttling logs one warning, not one per call.
    const retryLog = new GraphRetryLog();
    const graphOptions: GraphCallOptions = { retryLog };

    let results: SyncResults;
    try {
      const changes = await identifyAllChanges((message) => {
        updateProgressToast(
          progressToastId,
          message,
          "Identificerer ændringer"
        );
      }, graphOptions);

      if (!hasChangesToSync(changes)) {
        const { toast } = await import("@capacit-com/lara-law-shared-ui");
        toast.dismiss(progressToastId);
        showSuccessToast(
          "Ingen filer eller mapper at synkronisere.",
          "Synkroniseringsrapport"
        );
        return;
      }

      // Phase 2: Mutation
      setDocumentsSynchronizing(
        changes.existingDocumentsToSync.map((doc) => doc.id)
      );

      results = await executeSyncMutations(changes, {
        progressToastId,
        parentFolderId: changes.newDocuments[0]?.parentFolderId,
        graphOptions,
      });
    } finally {
      retryLog.flush({ workspaceId: workspace.id });
    }

    // Show completion message
    updateProgressToast(
      progressToastId,
      "Færdiggør synkronisering...",
      "Færdig"
    );

    await new Promise((resolve) => setTimeout(resolve, 500));

    // Dismiss progress toast
    const { toast } = await import("@capacit-com/lara-law-shared-ui");
    toast.dismiss(progressToastId);

    showSyncReport(results, {
      workspaceId: workspace.id,
      workspaceName: workspace.name,
    });
    setDocumentsSynchronizing([]);
  };

  /**
   * Uploads a local document to SharePoint for the first time
   */
  const handleInitialSharePointSync = async (
    document: WorkspaceDocument
  ): Promise<{
    title: string;
    description: string;
    variant: SyncResultVariant;
  }> => {
    setDocumentsSynchronizing((prev) => [...prev, document.id]);

    const file = await downloadFile(document.blobName);
    if (!file) {
      setDocumentsSynchronizing((prev) =>
        prev.filter((docId) => docId !== document.id)
      );
      return {
        title: "Fejl",
        description: "Kunne ikke finde filen på Azure.",
        variant: SyncResultVariant.DESTRUCTIVE,
      };
    }

    const patchedFile = new File([file], document.name, {
      type: file.type,
    });
    const result = await uploadFileToSharePoint(patchedFile);

    if (!result?.id || !result.parentReference?.driveId) {
      setDocumentsSynchronizing((prev) =>
        prev.filter((id) => id !== document.id)
      );
      return {
        title: "Fejl ved upload",
        description: "Filen kunne ikke uploades til SharePoint.",
        variant: SyncResultVariant.DESTRUCTIVE,
      };
    }

    const documentSource =
      result.parentReference.driveType === "documentLibrary"
        ? "SharePoint"
        : "OneDrive";

    const metadataBody = {
      documentSource: documentSource,
      driveId: result.parentReference.driveId,
      siteId: result.parentReference.siteId,
      sharepointFileId: result.id,
    };

    await handleUpdateDocumentMetadata(document.id, metadataBody);

    setDocumentsSynchronizing((prev) =>
      prev.filter((id) => id !== document.id)
    );

    return {
      title: "Upload gennemført",
      description: "Filen er nu synkroniseret med SharePoint og kan redigeres.",
      variant: SyncResultVariant.DEFAULT,
    };
  };

  /**
   * Deletes selected files from the workspace (used by deleted files dialog)
   */
  const handleDeleteSelectedFiles = async (documentIds: string[]) => {
    if (!workspace?.id) return;

    setIsDeletingFiles(true);
    try {
      await handleDeleteDocuments(workspace.id, documentIds, {
        successToastMessage: null,
        errorToastMessage: `Fejl ved sletning af fil${
          documentIds.length === 1 ? "" : "er"
        }`,
      });
      removeDeletedFiles(documentIds);
      if (deletedFiles.length === documentIds.length) {
        setDeletedFilesDialogOpen(false);
      }
    } catch {
      // Error toast already shown by handleDeleteDocuments
    } finally {
      setIsDeletingFiles(false);
    }
  };

  /**
   * Deletes documents from the workspace without touching the deleted-files-dialog
   * store. Used by the inline missing-files section inside SyncReportDialog.
   */
  const handleDeleteMissingDocuments = async (documentIds: string[]) => {
    if (!workspace?.id) return;
    await handleDeleteDocuments(workspace.id, documentIds, {
      successToastMessage: null,
      errorToastMessage: `Fejl ved sletning af fil${documentIds.length === 1 ? "" : "er"}`,
    });
  };

  return {
    handleSharePointFileSyncClick,
    handleSyncAll,
    handleSyncFolder,
    handleUnlinkFolder,
    documentsSynchronizing,
    handleInitialSharePointSync,
    handleDeleteSelectedFiles,
    handleDeleteMissingDocuments,
  };
};

export default useFileSync;
